/**
 * Member Import Parser
 *
 * Parses member export files (CSV/XLS/XLSX) into structured member data.
 * Uses a pluggable ImportSourceConfig to support different import sources
 * (Meetup, Beehiiv, etc.) without changing the parsing infrastructure.
 */

/** Configuration for a specific import source */
export interface ImportSourceConfig {
    /** Human-readable name for UI/logs */
    name: string
    /** Column header variations mapped to canonical keys (lowercase header -> key) */
    columnMap: Record<string, string>
    /** Map a source-specific role/title to a TinyClub role */
    mapRole: (value: string | undefined | null) => "cohost" | "member"
    /** Which canonical key to use as sourceId for deduplication (e.g., "userId") */
    sourceIdField?: string
    /** Which canonical keys are source-specific metadata (everything else goes in too) */
    metadataFields: string[]
}

/** A single parsed member from an import file */
export interface ParsedImportMember {
    /** Source-specific ID for deduplication */
    sourceId?: string
    /** Full display name */
    name: string
    /** Heuristic first name */
    firstName: string
    /** Heuristic last name */
    lastName: string
    /** Email address */
    email?: string
    /** Mapped TinyClub role */
    role: "cohost" | "member"
    /** All source-specific fields */
    sourceMetadata: Record<string, unknown>
    /** Any warnings generated during parsing */
    parseWarnings: string[]
}

/** Result of parsing an import file */
export interface ParseImportFileResult {
    /** Successfully parsed members */
    members: ParsedImportMember[]
    /** Total rows found in file */
    totalRows: number
    /** Number of rows skipped (e.g., missing name) */
    skippedRows: number
    /** File-level warnings */
    warnings: string[]
    /** Detected file type */
    fileType: "xls" | "xlsx" | "csv"
}

/**
 * Split a full name into first and last name.
 * - 1 word: all first name
 * - 2 words: first / last
 * - 3+ words: all-but-last / last
 */
export function splitName(fullName: string): { firstName: string; lastName: string } {
    const trimmed = fullName.trim()
    if (!trimmed) return { firstName: "", lastName: "" }

    const parts = trimmed.split(/\s+/)
    if (parts.length === 1) {
        return { firstName: parts[0], lastName: "" }
    }
    if (parts.length === 2) {
        return { firstName: parts[0], lastName: parts[1] }
    }
    // 3+ words: everything except last word is first name
    return {
        firstName: parts.slice(0, -1).join(" "),
        lastName: parts[parts.length - 1],
    }
}

/**
 * Map a Meetup title/role to a TinyClub role.
 * Organizer/Co-organizer/Assistant Organizer -> cohost
 * Everything else -> member
 */
export function mapMeetupRole(title: string | undefined | null): "cohost" | "member" {
    if (!title) return "member"
    const normalized = title.trim().toLowerCase()
    if (
        normalized === "organizer" ||
        normalized === "co-organizer" ||
        normalized === "assistant organizer"
    ) {
        return "cohost"
    }
    return "member"
}

/** Pre-built config for Meetup.com exports */
export const meetupSourceConfig: ImportSourceConfig = {
    name: "Meetup",
    columnMap: {
        name: "name",
        "member name": "name",
        "user id": "userId",
        "member id": "userId",
        userid: "userId",
        title: "title",
        role: "title",
        "url of member profile": "profileUrl",
        "member profile url": "profileUrl",
        "profile url": "profileUrl",
        location: "location",
        city: "location",
        "joined group on": "joinedGroupOn",
        "joined on": "joinedGroupOn",
        "join date": "joinedGroupOn",
        "meetups attended": "meetupsAttended",
        attended: "meetupsAttended",
        "total rsvps": "totalRsvps",
        rsvps: "totalRsvps",
        email: "email",
        "email address": "email",
    },
    mapRole: mapMeetupRole,
    sourceIdField: "userId",
    metadataFields: ["title", "profileUrl", "location", "joinedGroupOn", "meetupsAttended", "totalRsvps"],
}

// --- Core fields that are NOT metadata ---
const coreCanonicalKeys = new Set(["name", "email"])

function normalizeColumnName(header: string, config: ImportSourceConfig): string | undefined {
    return config.columnMap[header.trim().toLowerCase()]
}

function mapRow(row: Record<string, unknown>, config: ImportSourceConfig): ParsedImportMember | null {
    const mapped: Record<string, string> = {}
    const extraFields: Record<string, unknown> = {}
    const warnings: string[] = []

    for (const [key, value] of Object.entries(row)) {
        const canonical = normalizeColumnName(key, config)
        if (canonical && value != null && String(value).trim() !== "") {
            mapped[canonical] = String(value).trim()
        } else if (value != null && String(value).trim() !== "") {
            extraFields[key] = value
        }
    }

    // Name is required
    if (!mapped.name) return null

    const { firstName, lastName } = splitName(mapped.name)

    // Find the role field — look for the value that mapRole will use
    // The role field is typically "title" for Meetup, but we find it via the mapped values
    const roleFieldKey = Object.values(config.columnMap).find(
        (v) => v === "title" || v === "role"
    )
    const role = config.mapRole(roleFieldKey ? mapped[roleFieldKey] : undefined)

    // Build sourceId
    const sourceId = config.sourceIdField ? mapped[config.sourceIdField] : undefined

    // Build sourceMetadata from metadata fields + extra unmapped fields
    const sourceMetadata: Record<string, unknown> = {}
    const metadataFieldSet = new Set(config.metadataFields)

    for (const [key, value] of Object.entries(mapped)) {
        if (coreCanonicalKeys.has(key)) continue
        if (key === config.sourceIdField) continue
        if (metadataFieldSet.has(key)) {
            // Parse numeric fields
            const numValue = parseInt(value, 10)
            sourceMetadata[key] = !isNaN(numValue) && String(numValue) === value ? numValue : value
        } else if (key !== "name" && key !== "email") {
            // Any other mapped field goes in metadata too
            sourceMetadata[key] = value
        }
    }

    // Add unmapped extra fields to metadata
    for (const [key, value] of Object.entries(extraFields)) {
        sourceMetadata[key] = value
    }

    return {
        sourceId,
        name: mapped.name,
        firstName,
        lastName,
        email: mapped.email,
        role,
        sourceMetadata,
        parseWarnings: warnings,
    }
}

/**
 * Deduplicate members by sourceId.
 * If no sourceId, members are kept (can't dedup without ID).
 * Keeps the first occurrence.
 */
export function deduplicateMembers(members: ParsedImportMember[]): ParsedImportMember[] {
    const seen = new Set<string>()
    return members.filter((member) => {
        if (!member.sourceId) return true
        if (seen.has(member.sourceId)) return false
        seen.add(member.sourceId)
        return true
    })
}

/**
 * Parse a CSV string into members using the given source config.
 */
export function parseImportCsv(text: string, config: ImportSourceConfig): ParseImportFileResult {
    const lines = text.split(/\r?\n/)
    const warnings: string[] = []

    if (lines.length < 2) {
        return { members: [], totalRows: 0, skippedRows: 0, warnings: ["File appears to be empty"], fileType: "csv" }
    }

    // Parse header
    const headers = parseCSVLine(lines[0])
    const members: ParsedImportMember[] = []
    let skippedRows = 0
    let totalRows = 0

    for (let i = 1; i < lines.length; i++) {
        const line = lines[i].trim()
        if (!line) continue
        totalRows++

        const values = parseCSVLine(line)
        const row: Record<string, string> = {}
        headers.forEach((h, idx) => {
            if (idx < values.length) {
                row[h] = values[idx]
            }
        })

        const member = mapRow(row, config)
        if (member) {
            members.push(member)
        } else {
            skippedRows++
        }
    }

    return {
        members: deduplicateMembers(members),
        totalRows,
        skippedRows,
        warnings,
        fileType: "csv",
    }
}

/** Simple CSV line parser that handles quoted fields */
function parseCSVLine(line: string): string[] {
    const result: string[] = []
    let current = ""
    let inQuotes = false

    for (let i = 0; i < line.length; i++) {
        const ch = line[i]
        if (inQuotes) {
            if (ch === '"') {
                if (i + 1 < line.length && line[i + 1] === '"') {
                    current += '"'
                    i++ // skip next quote
                } else {
                    inQuotes = false
                }
            } else {
                current += ch
            }
        } else {
            if (ch === '"') {
                inQuotes = true
            } else if (ch === ",") {
                result.push(current.trim())
                current = ""
            } else {
                current += ch
            }
        }
    }
    result.push(current.trim())
    return result
}

/**
 * Parse an XLS/XLSX workbook into members using the given source config.
 * Uses the xlsx library (SheetJS) which must be provided.
 */
export function parseImportXls(
    workbook: { SheetNames: string[]; Sheets: Record<string, unknown> },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    xlsxUtils: { sheet_to_json: (sheet: any) => Record<string, unknown>[] },
    config: ImportSourceConfig
): ParseImportFileResult {
    const warnings: string[] = []

    if (!workbook.SheetNames.length) {
        return { members: [], totalRows: 0, skippedRows: 0, warnings: ["No sheets found in workbook"], fileType: "xls" }
    }

    const sheetName = workbook.SheetNames[0]
    const sheet = workbook.Sheets[sheetName]
    const rows = xlsxUtils.sheet_to_json(sheet) as Record<string, unknown>[]

    const members: ParsedImportMember[] = []
    let skippedRows = 0

    for (const row of rows) {
        const member = mapRow(row, config)
        if (member) {
            members.push(member)
        } else {
            skippedRows++
        }
    }

    return {
        members: deduplicateMembers(members),
        totalRows: rows.length,
        skippedRows,
        warnings,
        fileType: "xls",
    }
}

/**
 * Parse an import file (XLS, XLSX, or CSV).
 * Auto-detects format by file extension.
 * For XLS/XLSX, dynamically imports the xlsx library.
 */
export async function parseImportFile(file: File, config: ImportSourceConfig): Promise<ParseImportFileResult> {
    const extension = file.name.split(".").pop()?.toLowerCase()

    if (extension === "csv") {
        const text = await file.text()
        return parseImportCsv(text, config)
    }

    if (extension === "xls" || extension === "xlsx") {
        // Dynamic import to avoid 1.2MB bundle impact
        const XLSX = await import("xlsx")
        const buffer = await file.arrayBuffer()
        const workbook = XLSX.read(buffer, { type: "array" })
        const result = parseImportXls(workbook, XLSX.utils, config)
        result.fileType = extension
        return result
    }

    return {
        members: [],
        totalRows: 0,
        skippedRows: 0,
        warnings: [`Unsupported file format: .${extension}. Please upload a .xls, .xlsx, or .csv file.`],
        fileType: "csv",
    }
}
